import type { ExposureBand } from "../scoring";

/**
 * Exposure projection, on the SAME chart chassis as the sibling quiz's
 * manly/report/ProjectionChart.tsx: identical geometry, gradient, pills and
 * the pathLength/data-qk-curve draw-on hook (the animation rule in the shared
 * base stylesheet targets `.qk-manly [data-qk-curve]`, which this funnel's
 * shell already provides). What differs is only vocabulary: the y axis is his
 * DAILY EXPOSURE (what goes on him), never his hormones or his symptoms, so
 * the chart stays inside the causation refusal the result page makes.
 */

/** Start height per exposure band. 1 = heaviest daily load (top), 0 = clean routine (bottom). */
const START: Record<ExposureBand, number> = {
  SATURATED: 0.93,
  SUSTAINED: 0.74,
  ACTIVE: 0.55,
};
const END = 0.1;

const W = 400, H = 300, PAD_L = 60, PAD_R = 16, PAD_T = 30, PAD_B = 40;
const innerW = W - PAD_L - PAD_R;
const innerH = H - PAD_T - PAD_B;
const yTop = PAD_T + 14;
const yMid = PAD_T + innerH * 0.5;
const yBot = PAD_T + innerH - 14;
const fracToY = (f: number) => yBot + (yTop - yBot) * Math.max(0, Math.min(1, f));

const BANDS = [
  { y: yTop, a: "HIGH", b: "DAILY" },
  { y: yMid, a: "REDUCED", b: "MEASURED" },
  { y: yBot, a: "CLEAN", b: "ROUTINE" },
];
const X_LABELS = ["TODAY", "DAY 3", "WEEK 1", "WEEK 2"];

/** Pure: daily-exposure projection. Descends from "you are here" to a clean routine. */
export function ProjectionChart({ band }: { band: ExposureBand }) {
  const xStart = PAD_L + 6;
  const xEnd = PAD_L + innerW - 6;
  const startY = fracToY(START[band] ?? START.SUSTAINED);
  const endY = fracToY(END);

  const c1x = xStart + innerW * 0.4;
  const c2x = xEnd - innerW * 0.3;
  const c2y = endY + (startY - endY) * 0.28;
  const curve = `M ${xStart} ${startY} C ${c1x} ${startY}, ${c2x} ${c2y}, ${xEnd} ${endY}`;
  const area = `${curve} L ${xEnd} ${yBot + 6} L ${xStart} ${yBot + 6} Z`;
  const xs = [xStart, PAD_L + innerW * 0.34, PAD_L + innerW * 0.67, xEnd];

  const pill = (dotX: number, dotY: number, text: string, fill: string, textFill: string) => {
    const w = text.length * 6.6 + 20;
    const x = Math.max(PAD_L - 4, Math.min(W - PAD_R + 4 - w, dotX - w / 2));
    const top = dotY - 12 - 20;
    return (
      <g>
        <line x1={dotX} y1={dotY - 5} x2={dotX} y2={top + 20} stroke={fill} strokeWidth={1.5} opacity={0.7} />
        <rect x={x} y={top} width={w} height={20} rx={10} fill={fill} />
        <text x={x + w / 2} y={top + 13.5} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={textFill}>
          {text}
        </text>
      </g>
    );
  };

  return (
    <svg className="qk-manly-chart" viewBox={`0 0 ${W} ${H}`} role="img"
         aria-label="Projected daily exposure over the next two weeks">
      <defs>
        {/* Own gradient id: qk-manly-fill belongs to the sibling quiz's chart,
            and duplicate SVG ids collide if both funnels ever share a page. */}
        <linearGradient id="qk-mh-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-accent)" stopOpacity="0.55" />
          <stop offset="100%" stopColor="var(--color-accent)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {BANDS.map((b) => (
        <g key={b.a}>
          <line x1={PAD_L} y1={b.y} x2={W - PAD_R} y2={b.y}
                stroke="rgba(242,236,224,0.35)" strokeWidth={1} strokeDasharray="2 4" />
          <text x={PAD_L - 12} y={b.y - 2} textAnchor="end" fontSize={10.5} fontWeight={700}
                fill="rgba(242,236,224,0.85)">{b.a}</text>
          <text x={PAD_L - 12} y={b.y + 10} textAnchor="end" fontSize={8.5}
                fill="rgba(242,236,224,0.45)">{b.b}</text>
        </g>
      ))}

      {xs.map((x, i) => (
        <text key={X_LABELS[i]} x={x} y={H - 14} fontSize={10} fontWeight={600}
              textAnchor={i === 0 ? "start" : i === xs.length - 1 ? "end" : "middle"}
              fill="rgba(242,236,224,0.6)">{X_LABELS[i]}</text>
      ))}

      <path d={area} fill="url(#qk-mh-fill)" />
      {/* pathLength normalizes the dash math to 1 so the draw-on animation in
          the base stylesheet works regardless of the curve's real length. */}
      <path data-qk-curve="" pathLength={1} d={curve} fill="none" stroke="var(--color-accent)" strokeWidth={3} strokeLinecap="round" />

      <circle cx={xStart} cy={startY} r={6} fill="var(--color-accent-strong)" stroke="var(--color-accent)" strokeWidth={2.5} />
      <circle cx={xEnd} cy={endY} r={6.5} fill="var(--color-accent)" />

      {pill(xStart, startY, "YOU ARE HERE", "var(--color-accent-strong)", "var(--color-surface)")}
      {pill(xEnd, endY, "GOAL · CLEAN ROUTINE", "var(--color-accent)", "var(--color-accent-strong)")}
    </svg>
  );
}
